import { useState } from 'react';
import { Button, Dialog, Field, Input, useToast } from '@/components/ui';
import { useImagingActions } from '@/features/orders/api';
import {
  IMAGING_STATUS_LABEL,
  type ImagingReferral,
} from '@/features/orders/types';
import { isApiError } from '@/lib/api';
import { CLINIC_TZ, fromClinicInput, toClinicInput } from '@/lib/clinic-time';
import styles from '@/pages/queue/queue.module.css';

/** Imaging referral at the lab desk (artifact #115). */
export function ImagingDialog({
  referral: r,
  onClose,
}: {
  referral: ImagingReferral;
  onClose: () => void;
}) {
  const a = useImagingActions();
  const toast = useToast();
  // RL-1896: the field shows and saves clinic time (the ISO is UTC).
  const [at, setAt] = useState(toClinicInput(r.appointmentAt));
  const [note, setNote] = useState(r.appointmentNote ?? '');
  const [report, setReport] = useState(r.reportText ?? '');
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  const done = r.status === 'reviewed';
  return (
    <Dialog
      open
      title={`${r.exam ?? 'Imaging'} · ${r.patient.fullName}`}
      onClose={onClose}
      footer={
        !done && (
          <>
            <Button
              disabled={!at}
              loading={a.appointment.isPending}
              onClick={() =>
                a.appointment
                  .mutateAsync({
                    id: r.documentId,
                    at: fromClinicInput(at),
                    note: note.trim() || undefined,
                  })
                  .then(onClose)
                  .catch(fail)
              }
            >
              Appointment made
            </Button>
            <Button
              variant="primary"
              disabled={report.trim().length < 5}
              loading={a.report.isPending}
              onClick={() =>
                a.report
                  .mutateAsync({ id: r.documentId, report: report.trim() })
                  .then(() => {
                    toast.show({
                      tone: 'success',
                      title: `Report sent to ${r.doctor.fullName}`,
                    });
                    onClose();
                  })
                  .catch(fail)
              }
            >
              Attach report
            </Button>
          </>
        )
      }
    >
      <div className={styles.drawer}>
        <dl className={styles.rows}>
          <dt>Referred to</dt>
          <dd>{r.referredTo ?? '—'}</dd>
          <dt>Reason</dt>
          <dd>{r.reason ?? '—'}</dd>
          <dt>Referred</dt>
          <dd>
            {new Date(r.referredAt).toLocaleDateString('en-GB', {
              timeZone: CLINIC_TZ,
            })}{' '}
            · {r.doctor.fullName}
          </dd>
          <dt>Status</dt>
          <dd>{IMAGING_STATUS_LABEL[r.status]}</dd>
        </dl>
        {!done && (
          <>
            <Field label="Appointment">
              <Input
                type="datetime-local"
                value={at}
                onChange={(ev) => setAt(ev.target.value)}
              />
            </Field>
            <Field label="Appointment note">
              <Input value={note} onChange={(ev) => setNote(ev.target.value)} />
            </Field>
          </>
        )}
        <Field
          label="Report"
          hint="Paste or type the report as received; it goes to the doctor."
        >
          <textarea
            className={styles.textarea}
            rows={5}
            disabled={done}
            value={report}
            onChange={(ev) => setReport(ev.target.value)}
          />
        </Field>
        {r.reviewedAt && (
          <p className={styles.sub}>
            Reviewed by the doctor
            {r.reviewComment ? `: ${r.reviewComment}` : ''}
          </p>
        )}
      </div>
    </Dialog>
  );
}
